import { useTranslation } from 'react-i18next'
import { Box, Button, Card, CardContent, Chip, Grid, Stack, Typography } from '@mui/material'
import { PatientMatchCandidate, SyncConflict } from '@/core/offline'

export type PatientMatchCandidatesProps = {
  conflict: SyncConflict
  loading?: boolean
  onResolvePatientDuplicate: (conflictId: string, selectedPatientId: string) => void
}

export function PatientMatchCandidates({ conflict, loading, onResolvePatientDuplicate }: PatientMatchCandidatesProps) {
  const { t } = useTranslation()

  if (conflict.conflictType !== 'duplicate' || !conflict.duplicateCandidates) return null

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="subtitle2" gutterBottom>
        {t('sync.conflict.potentialMatches')}:
      </Typography>
      <Grid container spacing={2}>
        {conflict.duplicateCandidates.map((candidate: PatientMatchCandidate, index: number) => (
          <Grid item xs={12} md={6} key={candidate.patient._id || index}>
            <Card variant="outlined" sx={{ cursor: 'pointer' }}>
              <CardContent>
                <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                  <Box>
                    <Typography variant="h6">
                      {candidate.patient.userInfo?.name} {candidate.patient.userInfo?.surname}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {t('sync.conflict.birthDate')}: {candidate.patient.userInfo?.birthday}
                    </Typography>
                    {candidate.patient.secu && (
                      <Typography variant="body2" color="text.secondary">
                        {t('sync.conflict.socialSecurity')}: {candidate.patient.secu}
                      </Typography>
                    )}
                    {candidate.patient.contactInfo?.phone && (
                      <Typography variant="body2" color="text.secondary">
                        {t('sync.conflict.phone')}: {candidate.patient.contactInfo?.phone}
                      </Typography>
                    )}
                  </Box>
                  <Chip
                    label={`${Math.round(candidate.score * 100)}% ${t('sync.conflict.match')}`}
                    color={candidate.score > 0.8 ? 'error' : candidate.score > 0.6 ? 'warning' : 'info'}
                    size="small"
                  />
                </Stack>
                <Box sx={{ mt: 2 }}>
                  <Button variant="contained" size="small" onClick={() => onResolvePatientDuplicate(conflict._id, candidate.patient._id)} disabled={loading}>
                    {t('sync.conflict.useThisPatient')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  )
}
